import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, Circle, Loader2, ArrowRight, ArrowLeft,
  Users, Key, MessageSquare, GitBranch, Zap, Database, ChevronRight,
  Shield, Sparkles, Check, Lock, Mail, Bot
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { cn } from '../lib/utils';
import { useOnboarding } from '../context/OnboardingContext';
import { toast } from 'sonner';
import { getLogoByIntegrationId } from '../constants/logos';

const setupSteps = [
  {
    id: 'teams',
    title: 'Create Your First Team',
    description: 'Set up your IT operations team structure',
    icon: Users,
    estimatedTime: '2 min',
    required: true,
    dependencies: [],
  },
  {
    id: 'identity',
    title: 'Connect Identity Provider',
    description: 'Import users and enable SSO',
    icon: Key,
    estimatedTime: '3 min',
    required: true,
    dependencies: ['teams'],
  },
  {
    id: 'channels',
    title: 'Set Up Help Desk Channel',
    description: 'Connect Slack, Teams, or Email for intake',
    icon: MessageSquare,
    estimatedTime: '2 min',
    required: true,
    dependencies: ['teams', 'identity'],
  },
  {
    id: 'routing',
    title: 'Configure Request Routing',
    description: 'Define how requests flow to your team',
    icon: GitBranch,
    estimatedTime: '2 min',
    required: true,
    dependencies: ['teams'],
  },
  {
    id: 'workflow',
    title: 'Activate First Workflow',
    description: 'Enable an automation from our library',
    icon: Zap,
    estimatedTime: '1 min',
    required: true,
    dependencies: ['teams', 'routing'],
  },
  {
    id: 'hris',
    title: 'Connect HRIS & MDM',
    description: 'Enable employee lifecycle automation',
    icon: Database,
    estimatedTime: '5 min',
    required: false,
    dependencies: ['identity'],
  }
];

const idpOptions = [
  { id: 'okta', name: 'Okta', logoId: 'okta' },
  { id: 'azure', name: 'Microsoft Entra ID', logoId: 'entra-id' },
  { id: 'google', name: 'Google Workspace', logoId: 'google-workspace' },
  { id: 'onelogin', name: 'OneLogin', logoId: null },
];

const channelOptions = [
  { id: 'slack', name: 'Slack', icon: MessageSquare, description: 'Best for teams already using Slack' },
  { id: 'teams', name: 'Microsoft Teams', icon: MessageSquare, description: 'Best for Microsoft 365 organizations' },
  { id: 'email', name: 'Email', icon: Mail, description: 'Universal compatibility' },
];

const workflowOptions = [
  { id: 'password-reset', name: 'Password Reset Assistance', category: 'Identity', popular: true },
  { id: 'app-access', name: 'Application Access Request', category: 'Access', popular: true },
  { id: 'new-hire', name: 'New Hire Onboarding', category: 'Lifecycle', popular: false },
  { id: 'equipment', name: 'Equipment Request', category: 'Hardware', popular: false },
];

const statusConfig = {
  not_started: { icon: Circle, color: 'text-muted-foreground', bgColor: 'bg-muted', label: 'Not Started' },
  in_progress: { icon: Loader2, color: 'text-amber-600', bgColor: 'bg-amber-100', label: 'In Progress' },
  complete: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100', label: 'Complete' },
  locked: { icon: Lock, color: 'text-muted-foreground', bgColor: 'bg-muted/50', label: 'Locked' }
};

const isStepUnlocked = (stepId, completedSteps) => {
  const step = setupSteps.find(s => s.id === stepId);
  if (!step || !step.dependencies || step.dependencies.length === 0) return true;
  return step.dependencies.every(depId => completedSteps.includes(depId));
};

const getStepStatus = (stepId, completedSteps, currentStepId) => {
  if (completedSteps.includes(stepId)) return 'complete';
  if (stepId === currentStepId) return 'in_progress';
  if (!isStepUnlocked(stepId, completedSteps)) return 'locked';
  return 'not_started';
};

export const Setup = () => {
  const navigate = useNavigate();
  const { setupProgress, updateStepProgress, resetBannerDismissal } = useOnboarding();

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [showWelcome, setShowWelcome] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  const [teamName, setTeamName] = useState('IT Operations');
  const [adminEmail, setAdminEmail] = useState('');
  const [selectedIdp, setSelectedIdp] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('');
  const [channelName, setChannelName] = useState('#it-help');
  const [routingMode, setRoutingMode] = useState('auto');
  const [selectedWorkflow, setSelectedWorkflow] = useState('password-reset');

  const completedSteps = useMemo(() => {
    return setupSteps
      .filter(step => {
        const stepProgress = setupProgress[step.id] || {};
        const checks = ['team_created', 'admin_assigned', 'member_added', 'idp_connected', 'users_synced', 'sso_tested', 'channel_connected', 'channel_mode_set', 'test_message_sent', 'routing_rule_created', 'approval_chain_set', 'escalation_defined', 'workflow_created', 'workflow_tested', 'workflow_published', 'hris_connected', 'mdm_connected', 'employee_sync'];
        const relevantChecks = checks.filter(c => stepProgress[c] !== undefined);
        return relevantChecks.length > 0 && relevantChecks.every(c => stepProgress[c]);
      })
      .map(s => s.id);
  }, [setupProgress]);

  const requiredStepsComplete = useMemo(() => {
    return setupSteps.filter(s => s.required).every(s => completedSteps.includes(s.id));
  }, [completedSteps]);

  const overallProgress = Math.round((completedSteps.length / setupSteps.length) * 100);
  const currentStep = setupSteps[activeStepIndex];

  const markStepComplete = useCallback((stepId) => {
    const checkMappings = {
      teams: ['team_created', 'admin_assigned', 'member_added'],
      identity: ['idp_connected', 'users_synced', 'sso_tested'],
      channels: ['channel_connected', 'channel_mode_set', 'test_message_sent'],
      routing: ['routing_rule_created', 'approval_chain_set', 'escalation_defined'],
      workflow: ['workflow_created', 'workflow_tested', 'workflow_published'],
      hris: ['hris_connected', 'mdm_connected', 'employee_sync'],
    };

    const checks = checkMappings[stepId] || [];
    checks.forEach(checkId => {
      updateStepProgress(stepId, checkId, true);
    });
    resetBannerDismissal();
  }, [updateStepProgress, resetBannerDismissal]);

  const handleStepComplete = useCallback(async () => {
    setIsProcessing(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    markStepComplete(currentStep.id);

    toast.success(`${currentStep.title} complete!`, {
      description: 'Moving to next step...'
    });

    setIsProcessing(false);

    const nextIncompleteIndex = setupSteps.findIndex((s, idx) =>
      idx > activeStepIndex &&
      !completedSteps.includes(s.id) &&
      isStepUnlocked(s.id, [...completedSteps, currentStep.id])
    );

    if (nextIncompleteIndex !== -1) {
      setActiveStepIndex(nextIncompleteIndex);
    }
  }, [currentStep, activeStepIndex, completedSteps, markStepComplete]);

  const handleStepClick = (idx) => {
    const targetStep = setupSteps[idx];
    if (isStepUnlocked(targetStep.id, completedSteps)) {
      setActiveStepIndex(idx);
    }
  };

  const canCompleteCurrentStep = useMemo(() => {
    switch (currentStep.id) {
      case 'teams':
        return teamName.trim().length > 0;
      case 'identity':
        return selectedIdp !== '';
      case 'channels':
        return selectedChannel !== '' && channelName.trim().length > 0;
      case 'routing':
        return routingMode !== '';
      case 'workflow':
        return selectedWorkflow !== '';
      case 'hris':
        return true;
      default:
        return false;
    }
  }, [currentStep.id, teamName, selectedIdp, selectedChannel, channelName, routingMode, selectedWorkflow]);

  const renderStepContent = () => {
    switch (currentStep.id) {
      case 'teams':
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="teamName">Team Name</Label>
                <Input
                  id="teamName"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="e.g., IT Operations, Help Desk"
                />
                <p className="text-xs text-muted-foreground">This team will handle incoming IT requests</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="adminEmail">Your Email (Org Admin)</Label>
                <Input
                  id="adminEmail"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="you@company.com"
                />
                <p className="text-xs text-muted-foreground">You'll be assigned as the organization admin</p>
              </div>
            </div>

            <div className="p-4 bg-muted/50 rounded-lg">
              <h5 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                What happens next
              </h5>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Your team will be created with you as the admin</li>
                <li>• You can invite team members after connecting your IdP</li>
                <li>• Teams can be renamed or restructured anytime</li>
              </ul>
            </div>
          </div>
        );

      case 'identity':
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <Label>Select Your Identity Provider</Label>
              <div className="grid grid-cols-2 gap-3">
                {idpOptions.map((idp) => {
                  const logo = idp.logoId ? getLogoByIntegrationId(idp.logoId) : null;
                  return (
                    <button
                      key={idp.id}
                      onClick={() => setSelectedIdp(idp.id)}
                      className={cn(
                        "flex items-center gap-3 p-4 rounded-lg border text-left transition-all",
                        selectedIdp === idp.id
                          ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      {logo ? (
                        <img src={logo.src} alt={logo.alt} className="w-8 h-8 object-contain" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                          <Key className="w-4 h-4 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-sm">{idp.name}</p>
                      </div>
                      {selectedIdp === idp.id && (
                        <Check className="w-4 h-4 text-primary ml-auto" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="p-4 bg-muted/50 rounded-lg">
              <h5 className="text-sm font-medium mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4 text-primary" />
                Security Note
              </h5>
              <p className="text-sm text-muted-foreground">
                We use read-only OAuth scopes by default. Write permissions are only requested when you enable provisioning actions.
              </p>
            </div>
          </div>
        );

      case 'channels':
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <Label>Choose Your Primary Channel</Label>
              <div className="space-y-2">
                {channelOptions.map((channel) => {
                  const Icon = channel.icon;
                  return (
                    <button
                      key={channel.id}
                      onClick={() => setSelectedChannel(channel.id)}
                      className={cn(
                        "w-full flex items-center gap-3 p-4 rounded-lg border text-left transition-all",
                        selectedChannel === channel.id
                          ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{channel.name}</p>
                        <p className="text-xs text-muted-foreground">{channel.description}</p>
                      </div>
                      {selectedChannel === channel.id && (
                        <Check className="w-5 h-5 text-primary" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {selectedChannel && selectedChannel !== 'email' && (
              <div className="space-y-2">
                <Label htmlFor="channelName">Channel Name</Label>
                <Input
                  id="channelName"
                  value={channelName}
                  onChange={(e) => setChannelName(e.target.value)}
                  placeholder="#it-help"
                />
                <p className="text-xs text-muted-foreground">
                  We'll create this channel if it doesn't exist
                </p>
              </div>
            )}

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h5 className="text-sm font-medium text-amber-800 mb-1">Starting in Silent Mode</h5>
              <p className="text-sm text-amber-700">
                Your channel will start in Silent mode—we'll observe requests without responding, so you can review AI suggestions before going live.
              </p>
            </div>
          </div>
        );

      case 'routing':
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <Label>Request Routing Mode</Label>
              <div className="space-y-2">
                <button
                  onClick={() => setRoutingMode('auto')}
                  className={cn(
                    "w-full flex items-center gap-3 p-4 rounded-lg border text-left transition-all",
                    routingMode === 'auto'
                      ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm">Smart Auto-Routing</p>
                      <Badge variant="secondary" className="text-xs">Recommended</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">AI categorizes and routes requests to the right team member</p>
                  </div>
                  {routingMode === 'auto' && <Check className="w-5 h-5 text-primary" />}
                </button>

                <button
                  onClick={() => setRoutingMode('queue')}
                  className={cn(
                    "w-full flex items-center gap-3 p-4 rounded-lg border text-left transition-all",
                    routingMode === 'queue'
                      ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                    <Users className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">Team Queue</p>
                    <p className="text-xs text-muted-foreground">All requests go to a shared queue for manual assignment</p>
                  </div>
                  {routingMode === 'queue' && <Check className="w-5 h-5 text-primary" />}
                </button>
              </div>
            </div>

            <div className="p-4 bg-muted/50 rounded-lg">
              <h5 className="text-sm font-medium mb-2">Default Settings Applied</h5>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Escalation timeout: 4 hours</li>
                <li>• Sensitive actions require manager approval</li>
                <li>• After-hours requests queue for next business day</li>
              </ul>
              <p className="text-xs text-muted-foreground mt-2">
                You can customize these in Settings → Routing after setup
              </p>
            </div>
          </div>
        );

      case 'workflow':
        return (
          <div className="space-y-6">
            <div className="space-y-3">
              <Label>Choose Your First Workflow</Label>
              <div className="space-y-2">
                {workflowOptions.map((workflow) => (
                  <button
                    key={workflow.id}
                    onClick={() => setSelectedWorkflow(workflow.id)}
                    className={cn(
                      "w-full flex items-center gap-3 p-4 rounded-lg border text-left transition-all",
                      selectedWorkflow === workflow.id
                        ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                        : "border-border hover:border-primary/50"
                    )}
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{workflow.name}</p>
                        {workflow.popular && (
                          <Badge variant="secondary" className="text-xs">Popular</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">Category: {workflow.category}</p>
                    </div>
                    {selectedWorkflow === workflow.id && <Check className="w-5 h-5 text-primary" />}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h5 className="text-sm font-medium text-green-800 mb-1">Safe Activation</h5>
              <p className="text-sm text-green-700">
                This workflow will start in "Team Only" mode—it will only activate for your team members, not company-wide.
              </p>
            </div>
          </div>
        );

      case 'hris':
        return (
          <div className="space-y-6">
            <div className="p-4 bg-muted/50 rounded-lg">
              <h5 className="text-sm font-medium mb-2">Why Connect HRIS & MDM?</h5>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Automatic onboarding when new hires are added</li>
                <li>• Offboarding workflows triggered by terminations</li>
                <li>• Device enrollment synced with employee status</li>
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                { id: 'bamboohr', name: 'BambooHR', type: 'HRIS' },
                { id: 'workday', name: 'Workday', type: 'HRIS' },
                { id: 'intune', name: 'Microsoft Intune', type: 'MDM' },
                { id: 'jamf', name: 'Jamf', type: 'MDM' },
              ].map((system) => {
                const logo = getLogoByIntegrationId(system.id);
                return (
                  <div
                    key={system.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card"
                  >
                    {logo ? (
                      <img src={logo.src} alt={logo.alt} className="w-6 h-6 object-contain" />
                    ) : (
                      <Database className="w-6 h-6 text-muted-foreground" />
                    )}
                    <div>
                      <p className="text-sm font-medium">{system.name}</p>
                      <p className="text-xs text-muted-foreground">{system.type}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-sm text-muted-foreground text-center">
              This step is optional. You can configure HRIS & MDM integrations later from the Connectors page.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  if (showWelcome && completedSteps.length === 0) {
    return (
      <div className="min-h-full bg-background flex items-center justify-center p-6">
        <Card className="max-w-lg w-full">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl">Welcome to Vedaya</CardTitle>
            <CardDescription className="text-base">
              Let's get your IT help desk up and running. This quick setup takes about 10 minutes.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="space-y-3">
              {[
                { icon: Users, text: 'Create your first team' },
                { icon: Key, text: 'Connect your identity provider' },
                { icon: MessageSquare, text: 'Set up a help desk channel' },
                { icon: Zap, text: 'Activate your first automation' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 text-sm">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <item.icon className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button className="w-full" size="lg" onClick={() => setShowWelcome(false)}>
              Start Setup
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <button
              className="text-sm text-muted-foreground hover:text-foreground"
              onClick={() => {
                setupSteps.filter(s => s.required).forEach(s => markStepComplete(s.id));
                toast.success('Demo mode activated', {
                  description: 'All required setup steps have been completed.'
                });
                navigate('/control-tower');
              }}
            >
              Skip for demo →
            </button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Setup Wizard</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Get your help desk operational in minutes
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">{overallProgress}% Complete</p>
                <p className="text-xs text-muted-foreground">
                  {completedSteps.length}/{setupSteps.length} steps
                </p>
              </div>
              <div className="w-32">
                <Progress value={overallProgress} className="h-2" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Setup Steps</CardTitle>
                <CardDescription>Complete required steps to go live</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y">
                  {setupSteps.map((step, idx) => {
                    const status = getStepStatus(step.id, completedSteps, currentStep.id);
                    const config = statusConfig[status];
                    const Icon = config.icon;
                    const isActive = activeStepIndex === idx;
                    const isUnlocked = isStepUnlocked(step.id, completedSteps);

                    return (
                      <Tooltip key={step.id} delayDuration={300}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => handleStepClick(idx)}
                            className={cn(
                              "w-full flex items-center gap-3 p-4 text-left transition-colors",
                              isActive && "bg-muted",
                              isUnlocked ? "hover:bg-muted/50" : "opacity-60 cursor-not-allowed"
                            )}
                            disabled={!isUnlocked}
                          >
                            <div className={cn("w-8 h-8 rounded-full flex items-center justify-center shrink-0", config.bgColor)}>
                              <Icon className={cn("w-4 h-4", config.color, status === 'in_progress' && "animate-spin")} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={cn("text-sm font-medium truncate", isActive ? "text-foreground" : "text-muted-foreground")}>
                                  {step.title}
                                </span>
                                {!step.required && (
                                  <Badge variant="outline" className="text-xs shrink-0">Optional</Badge>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">{step.estimatedTime}</span>
                            </div>
                            <ChevronRight className={cn("w-4 h-4 shrink-0", isActive ? "text-foreground" : "text-muted-foreground")} />
                          </button>
                        </TooltipTrigger>
                        {!isUnlocked && (
                          <TooltipContent side="right">
                            <p className="text-sm">Complete previous steps first</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {requiredStepsComplete && (
              <Card className="mt-4 border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-green-900">Ready to Go Live!</p>
                      <p className="text-xs text-green-700 mt-1">
                        All required steps complete. Your help desk is ready.
                      </p>
                      <Button size="sm" className="mt-3" onClick={() => navigate('/control-tower')}>
                        Open Control Tower
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="col-span-8">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "w-12 h-12 rounded-lg flex items-center justify-center",
                      completedSteps.includes(currentStep.id) ? "bg-green-100" : "bg-primary/10"
                    )}>
                      <currentStep.icon className={cn(
                        "w-6 h-6",
                        completedSteps.includes(currentStep.id) ? "text-green-600" : "text-primary"
                      )} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle>{currentStep.title}</CardTitle>
                        {!currentStep.required && <Badge variant="outline">Optional</Badge>}
                        {completedSteps.includes(currentStep.id) && (
                          <Badge className="bg-green-100 text-green-700 border-0">Complete</Badge>
                        )}
                      </div>
                      <CardDescription className="mt-1">{currentStep.description}</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                {renderStepContent()}
              </CardContent>

              <CardFooter className="flex items-center justify-between border-t pt-6">
                <Button
                  variant="outline"
                  onClick={() => setActiveStepIndex(Math.max(0, activeStepIndex - 1))}
                  disabled={activeStepIndex === 0}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>

                <div className="flex items-center gap-2">
                  {!currentStep.required && !completedSteps.includes(currentStep.id) && (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        const nextIndex = setupSteps.findIndex((s, idx) =>
                          idx > activeStepIndex && !completedSteps.includes(s.id)
                        );
                        if (nextIndex !== -1) {
                          setActiveStepIndex(nextIndex);
                        }
                      }}
                    >
                      Skip for now
                    </Button>
                  )}

                  {completedSteps.includes(currentStep.id) ? (
                    <Button
                      onClick={() => {
                        const nextIndex = setupSteps.findIndex((s, idx) =>
                          idx > activeStepIndex && !completedSteps.includes(s.id)
                        );
                        if (nextIndex !== -1) {
                          setActiveStepIndex(nextIndex);
                        } else {
                          navigate('/control-tower');
                        }
                      }}
                    >
                      {activeStepIndex < setupSteps.length - 1 ? 'Next Step' : 'Finish Setup'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleStepComplete}
                      disabled={!canCompleteCurrentStep || isProcessing}
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Configuring...
                        </>
                      ) : (
                        <>
                          Complete Step
                          <CheckCircle className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Setup;
